/**
 * 顯示校準工具函數
 *
 * 將實際量測值映射到美化的目標範圍。
 * DB 原始數據完全不修改，僅在前端顯示層套用。
 */

/** 校準配置類型（與後端 calibration_config JSON 結構一致） */
export interface CalibrationConfig {
  enabled: boolean;
  ehi?: {
    target_min: number; // 預設 65
    target_max: number; // 預設 95
  };
  vibration?: {
    [machineId: string]: { scale_factor: number };
  };
  suppress_alarms?: boolean;
}

/** 預設啟用校準（與 DB 實際配置同步，避免載入期間閃現，dev 可隨時調整） */
export const DEFAULT_CALIBRATION: CalibrationConfig = {
  enabled: true,
  ehi: { target_min: 80, target_max: 95 },
  vibration: { '1': { scale_factor: 0.15 }, '2': { scale_factor: 0.5 } },
  suppress_alarms: true,
};

/**
 * EHI 線性映射
 * @param actual 0-100 百分制原始值（ehi_overall * 100）
 * @param config 校準配置
 * @returns 映射後的 EHI 值，或 null
 */
export function calibrateEhi(
  actual: number | null | undefined,
  config: CalibrationConfig,
): number | null {
  if (actual === null || actual === undefined) return null;
  // Prognosis 異常時回傳 -1 哨兵值（原始 0-1 → *100 後 < 0），視為無資料
  if (actual < 0) return null;
  if (!config.enabled || !config.ehi) return actual;
  const { target_min, target_max } = config.ehi;
  return target_min + (actual / 100) * (target_max - target_min);
}

/**
 * 振動速度縮放
 * @param actual 原始速度值 (mm/s)
 * @param machineId 機台 ID
 * @param config 校準配置
 * @returns 縮放後的值，或 null
 */
export function calibrateVibration(
  actual: number | null | undefined,
  machineId: number,
  config: CalibrationConfig,
): number | null {
  if (actual === null || actual === undefined) return null;
  if (!config.enabled || !config.vibration) return actual;
  const scale = config.vibration[String(machineId)]?.scale_factor;
  if (!scale) return actual;
  return actual * scale;
}
